import {
  Box,
  Button,
  Container,
  Grid,
  Heading,
  HStack,
  Icon,
  Image,
  Link,
  Text,
  VStack,
  useColorModeValue,
  SimpleGrid,
  Card,
  CardBody,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  FiCheckCircle,
  FiTrendingUp,
  FiClock,
  FiShield,
  FiZap,
  FiBarChart2,
  FiSmartphone,
  FiLayers,
  FiClipboard,
  FiMapPin,
} from 'react-icons/fi';
import {
  RiBuilding2Line,
  RiHospitalLine,
} from 'react-icons/ri';
import { GiFactory } from 'react-icons/gi';
import { MdEvent, MdFlight, MdHotel, MdRestaurant } from 'react-icons/md';
import { MarketingHeader } from '@/components/marketing/MarketingHeader';
import { MarketingFooter } from '@/components/marketing/MarketingFooter';
import { DemoRequestForm } from '@/components/marketing/DemoRequestForm';
import { TestimonialCard } from '@/components/marketing/TestimonialCard';
import { TESTIMONIALS } from '@/components/marketing/testimonials';
import { TaskeyeHeroVideo } from '@/components/marketing/TaskeyeHeroVideo';
import { SEO } from '@/components/SEO';
import { HOME_ORGANIZATION_JSONLD } from '@/lib/structuredData';

const SHOW_TESTIMONIALS = true;

// Industries TaskEye serves but doesn't lead with: linked from one compact row, below
// the full sections for the priority ones.
const MORE_INDUSTRIES: Array<{ icon: React.ElementType; title: string; description: string; path: string }> = [
  {
    icon: RiBuilding2Line,
    title: 'Construction & Development',
    description: 'Site inspections, safety checks and daily progress reports.',
    path: '/industries/construction',
  },
  {
    icon: RiHospitalLine,
    title: 'Healthcare & Hospitals',
    description: 'NABH and JCI quality checklists, sterilization and handover logs.',
    path: '/industries/healthcare',
  },
  {
    icon: MdFlight,
    title: 'Airports',
    description: 'Terminal cleanliness, security, facility and vendor checks.',
    path: '/industries/airports',
  },
];

export default function MarketingLandingPage() {
  const navigate = useNavigate();
  const bgGradient = useColorModeValue(
    'linear(to-br, brand.50, white, brand.50)',
    'linear(to-br, gray.900, gray.800, gray.900)'
  );
  const cardGradient = useColorModeValue(
    'linear(to-br, white, brand.50)',
    'linear(to-br, gray.800, gray.700)'
  );
  const sectionGradient = useColorModeValue(
    'linear(to-b, bg-canvas, bg-surface)',
    'linear(to-b, gray.900, gray.800)'
  );

  const scrollToDemoForm = () => {
    const demoSection = document.getElementById('demo-form');
    demoSection?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box bg="bg-canvas" minH="100vh">
      <SEO
        title="TaskEye – Digital SOP Checklists on WhatsApp"
        description="TaskEye runs checklists, field visits and team tasks on WhatsApp for restaurants, factories, events and field sales teams. No app to install."
        canonical="/"
        jsonLd={HOME_ORGANIZATION_JSONLD}
      />
      {/* Header/Navigation */}
      <MarketingHeader showDemoButton onDemoClick={scrollToDemoForm} />

      {/* Main Hero Section */}
      <Box bgGradient={bgGradient} pt={{ base: 12, md: 16 }} pb={{ base: 16, md: 24 }}>
        <Container maxW="7xl">
          <VStack spacing={12}>
            <VStack spacing={8} textAlign="center" maxW="4xl" mx="auto">
              <Heading
                as="h1"
                fontSize={{ base: '5xl', md: '6xl', lg: '7xl' }}
                fontWeight="800"
                lineHeight="1.1"
                color="text-primary"
                letterSpacing="tight"
              >
                Digital SOP Checklists on WhatsApp
              </Heading>
              <Text fontSize={{ base: 'lg', md: 'xl' }} color="text-secondary" maxW="3xl">
                TaskEye helps organizations digitize daily operations and data collection
                through simple WhatsApp-based workflows — without apps, hardware, or training.
              </Text>
              <HStack spacing={4} flexWrap="wrap" justify="center">
                <Button
                  size="lg"
                  colorScheme="brand"
                  rightIcon={<Icon as={FiCheckCircle} />}
                  onClick={scrollToDemoForm}
                  px={8}
                  py={6}
                  fontSize="lg"
                >
                  Request a Demo
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  colorScheme="brand"
                  onClick={() => navigate('/client/login')}
                  px={8}
                  py={6}
                  fontSize="lg"
                >
                  Sign In
                </Button>
              </HStack>
            </VStack>
            <Box w="full" maxW="6xl">
              <TaskeyeHeroVideo />
            </Box>
            {/* Industry Social Proof */}
            <VStack spacing={3} pt={4}>
              <Text fontSize="sm" color="text-muted" fontWeight="500">
                5K+ active users across
              </Text>
              <HStack spacing={6} flexWrap="wrap" justify="center" color="text-secondary" fontSize="sm">
                <HStack spacing={1}>
                  <Icon as={GiFactory} color="brand.500" w={4} h={4} />
                  <Text>Manufacturing</Text>
                </HStack>
                <HStack spacing={1}>
                  <Icon as={RiBuilding2Line} color="brand.500" w={4} h={4} />
                  <Text>Real Estate</Text>
                </HStack>
                <HStack spacing={1}>
                  <Icon as={RiHospitalLine} color="brand.500" w={4} h={4} />
                  <Text>Healthcare</Text>
                </HStack>
                <HStack spacing={1}>
                  <Icon as={MdHotel} color="brand.500" w={4} h={4} />
                  <Text>Hospitality</Text>
                </HStack>
              </HStack>
            </VStack>
          </VStack>
        </Container>
      </Box>

      {/* Domain-Specific Sections, in order of priority */}
      <DomainSection
        icon={MdRestaurant}
        title="Hospitality & Restaurants"
        subtitle="Digitize FSSAI Compliance, Stay Audit-Ready"
        description="Digitize daily hygiene checklists, temperature logs, and license renewal tracking for restaurants, cloud kitchens, and QSRs — so nothing slips past an FSSAI inspection or a delivery-aggregator compliance check."
        features={[
          'Daily hygiene & food handler checklist',
          'Temperature & cooling log',
          'Pest control schedule tracking',
          'Water quality test reminders',
          'License & FoSTaC expiry tracking',
          'Mock-audit self-inspection checklist',
        ]}
        stats={[
          { label: 'Setup', value: 'No App Needed' },
          { label: 'Task Order', value: 'Any-Order Execution' },
          { label: 'Records', value: 'Timestamped & Exportable' },
        ]}
        imageSrc="https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=800&q=80"
        industryPath="/industries/restaurants"
      />

      <DomainSection
        icon={GiFactory}
        title="Manufacturing & Shop Floor"
        subtitle="Precision Production, Zero Defects"
        description="Track production workflows, quality checks, and machine maintenance in real-time. Perfect for aerospace component manufacturers and auto parts suppliers."
        features={[
          'Real-time production tracking',
          'Quality control checklists',
          'Machine maintenance logs',
          'Compliance documentation',
          'Inventory management',
          'Shift handover automation',
        ]}
        stats={[
          { label: 'Setup', value: 'No App Needed' },
          { label: 'Production Tracking', value: 'Real-Time' },
          { label: 'Compliance', value: 'Audit-Ready' },
        ]}
        imageSrc="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&q=80"
        industryPath="/industries/manufacturing"
        reverse
      />

      <DomainSection
        icon={MdEvent}
        title="Events Management"
        subtitle="Flawless Events, Every Time"
        description="Coordinate vendors, manage schedules, and track every detail of your events with ease. From small corporate gatherings to large-scale conferences."
        features={[
          'Vendor coordination',
          'Attendee management',
          'Schedule tracking',
          'Safety checklists',
          'Budget tracking',
          'Post-event reports',
        ]}
        stats={[
          { label: 'Setup', value: 'No App Needed' },
          { label: 'Vendor Coordination', value: 'Real-Time' },
          { label: 'Task Tracking', value: 'Fully Logged' },
        ]}
        imageSrc="https://images.unsplash.com/photo-1511578314322-379afb476865?w=800&q=80"
        industryPath="/industries/events"
      />

      <DomainSection
        icon={FiMapPin}
        title="Field Sales & Field Agents"
        subtitle="Proof of Every Visit, No Tracking App"
        description="Location-verified check-ins at every store or site, photo proof, visit reports and attendance for field sales agents, merchandisers and technicians, all on WhatsApp. No background GPS tracking."
        features={[
          'Location-verified check-ins',
          'Nearest-store lookup',
          'Photo proof of every visit',
          'Visit report forms',
          'Attendance check-in and out',
          'Daily visit reports',
        ]}
        stats={[
          { label: 'Setup', value: 'No App Needed' },
          { label: 'Check-ins', value: 'Location-Verified' },
          { label: 'Visits', value: 'Photo Proof' },
        ]}
        imageSrc="https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=800&q=80"
        industryPath="/industries/field-agents"
        reverse
      />

      <DomainSection
        icon={FiClipboard}
        title="Team Tasks"
        subtitle="Assign & Track Tasks on WhatsApp"
        description="Assign a task to anyone on your team in one WhatsApp message. They get it, do it and mark it done, with no app to install, and you see live status without chasing anyone."
        features={[
          'Assign tasks in one message',
          'Reminders until done',
          'Pending and overdue at a glance',
          'Due dates on every task',
          'Full, attributed task history',
          'Keeps tasks out of busy groups',
        ]}
        stats={[
          { label: 'Setup', value: 'Live in a Day' },
          { label: 'Assigning', value: 'One Message' },
          { label: 'Status', value: 'Live Dashboard' },
        ]}
        imageSrc="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&q=80"
        industryPath="/whatsapp-task-management"
      />

      {/* Less-emphasised industries: one compact row of links */}
      <Box py={{ base: 12, md: 16 }} bg="bg-surface">
        <Container maxW="7xl">
          <VStack spacing={8}>
            <Heading size="lg" color="text-primary" textAlign="center">
              Also Used In
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} w="full">
              {MORE_INDUSTRIES.map(industry => (
                <Link
                  key={industry.path}
                  as={RouterLink}
                  to={industry.path}
                  _hover={{ textDecoration: 'none' }}
                >
                  <Card h="full" borderWidth="1px" borderColor="border-default" _hover={{ borderColor: 'brand.500' }}>
                    <CardBody>
                      <HStack align="start" spacing={4}>
                        <Icon as={industry.icon} w={8} h={8} color="brand.500" flexShrink={0} />
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="600" color="text-primary">{industry.title}</Text>
                          <Text fontSize="sm" color="text-secondary">{industry.description}</Text>
                        </VStack>
                      </HStack>
                    </CardBody>
                  </Card>
                </Link>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Features Grid */}
      <Box py={{ base: 16, md: 24 }} bgGradient={sectionGradient}>
        <Container maxW="7xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading size="xl" color="text-primary">
                Everything You Need to Automate Your Workflows
              </Heading>
              <Text fontSize="lg" color="text-secondary" maxW="2xl">
                Powerful features designed to save time, reduce errors, and boost productivity
                across your entire operation.
              </Text>
            </VStack>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} w="full">
              <FeatureCard
                icon={FiZap}
                title="Workflow Automation"
                description="Create custom checklists and workflows that guide your team through complex processes step-by-step."
              />
              <FeatureCard
                icon={FiBarChart2}
                title="Real-Time Analytics"
                description="Get instant visibility into operations with live dashboards and comprehensive reporting."
              />
              <FeatureCard
                icon={FiSmartphone}
                title="WhatsApp Integration"
                description="No new app to learn. Your team uses WhatsApp - the tool they already know and love."
              />
              <FeatureCard
                icon={FiLayers}
                title="ERP Integrations"
                description="Seamlessly connect with your existing ERP systems for unified data flow and operations."
              />
              <FeatureCard
                icon={FiClock}
                title="Mobile-First Access"
                description="Access workflows and data from anywhere, on any device."
              />
              <FeatureCard
                icon={FiShield}
                title="Compliance Tracking"
                description="Maintain audit trails and ensure regulatory compliance with automated documentation."
              />
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box py={{ base: 16, md: 24 }}>
        <Container maxW="7xl">
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={12} alignItems="center">
            <VStack align="start" spacing={6}>
              <Heading size="xl" color="text-primary">
                Why Leading SMBs Choose TaskEye
              </Heading>
              <VStack align="start" spacing={4}>
                <BenefitItem
                  title="40% Faster Task Completion"
                  description="Teams complete workflows faster with guided, automated processes."
                />
                <BenefitItem
                  title="Save 15+ Hours Per Week"
                  description="Eliminate manual data entry and repetitive administrative tasks."
                />
                <BenefitItem
                  title="Reduce Errors by 60%"
                  description="Standardized checklists ensure consistency and catch mistakes early."
                />
                <BenefitItem
                  title="Instant ROI Visibility"
                  description="Real-time dashboards show exactly where your team is saving time and money."
                />
              </VStack>
            </VStack>
            <Box
              bg="brand.50"
              _dark={{ bg: 'gray.800' }}
              borderRadius="2xl"
              p={8}
              h="400px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <VStack spacing={4}>
                <Icon as={FiTrendingUp} w={24} h={24} color="brand.500" />
                <Text fontSize="xl" fontWeight="600" color="text-primary" textAlign="center">
                  See Your ROI in Real-Time
                </Text>
              </VStack>
            </Box>
          </Grid>
        </Container>
      </Box>

      {/* Testimonials/Social Proof */}
      {SHOW_TESTIMONIALS && (
        <Box py={{ base: 16, md: 24 }} bgGradient={sectionGradient}>
          <Container maxW="7xl">
            <VStack spacing={12}>
              <VStack spacing={4} textAlign="center">
                <Heading size="xl" color="text-primary">
                  Testimonials
                </Heading>
                <Text fontSize="lg" color="text-secondary" maxW="2xl">
                  Hear from the teams running their operations on TaskEye every day.
                </Text>
              </VStack>
              <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={8} w="full">
                {TESTIMONIALS.map(testimonial => (
                  <TestimonialCard key={testimonial.name} {...testimonial} />
                ))}
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>
      )}

      {/* Final CTA Section */}
      <Box py={{ base: 16, md: 24 }} id="demo-form">
        <Container maxW="4xl">
          <Card bgGradient={cardGradient} borderWidth="1px" borderColor="border-default">
            <CardBody p={{ base: 8, md: 12 }}>
              <VStack spacing={8}>
                <VStack spacing={4} textAlign="center">
                  <Heading size="xl" color="text-primary">
                    Ready to Transform Your Operations?
                  </Heading>
                  <Text fontSize="lg" color="text-secondary">
                    Schedule a personalized demo and see how TaskEye can streamline your workflows
                    in under 30 days.
                  </Text>
                </VStack>
                <DemoRequestForm />
                <HStack spacing={8} pt={4} color="text-muted" fontSize="sm" flexWrap="wrap">
                  <HStack>
                    <Icon as={FiCheckCircle} color="brand.500" />
                    <Text>No credit card required</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiShield} color="brand.500" />
                    <Text>SOC 2 certified</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FiClock} color="brand.500" />
                    <Text>Setup in under 24 hours</Text>
                  </HStack>
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Box>

      {/* Footer */}
      <MarketingFooter />
    </Box>
  );
}

// Domain Section Component
interface DomainSectionProps {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  description: string;
  features: string[];
  stats: Array<{ label: string; value: string }>;
  imageSrc: string;
  industryPath: string;
  reverse?: boolean;
}

function DomainSection({
  icon,
  title,
  subtitle,
  description,
  features,
  stats,
  imageSrc,
  industryPath,
  reverse = false,
}: DomainSectionProps) {
  return (
    <Box py={{ base: 16, md: 24 }} bg={reverse ? 'bg-surface' : 'bg-canvas'}>
      <Container maxW="7xl">
        <Grid
          templateColumns={{ base: '1fr', lg: '1fr 1fr' }}
          gap={12}
          alignItems="center"
        >
          <VStack align={{ base: 'center', lg: 'start' }} spacing={6} order={{ base: 2, lg: reverse ? 2 : 1 }}>
            <VStack spacing={3} align={{ base: 'center', lg: 'start' }}>
              <Icon as={icon} w={10} h={10} color="brand.500" />
              <VStack align={{ base: 'center', lg: 'start' }} spacing={0}>
                <Link
                  as={RouterLink}
                  to={industryPath}
                  _hover={{ color: 'brand.600', textDecoration: 'underline' }}
                  color="inherit"
                >
                  <Heading size="xl" color="text-primary" textAlign={{ base: 'center', lg: 'left' }}>
                    {title}
                  </Heading>
                </Link>
                <Text fontSize="lg" color="brand.500" fontWeight="600" textAlign={{ base: 'center', lg: 'left' }}>
                  {subtitle}
                </Text>
              </VStack>
            </VStack>
            <Text fontSize="lg" color="text-secondary" textAlign={{ base: 'center', lg: 'left' }}>
              {description}
            </Text>
            <SimpleGrid columns={2} spacing={4} w="full">
              {features.map((feature, idx) => (
                <HStack key={idx} align="start">
                  <Icon as={FiCheckCircle} color="brand.500" mt={1} flexShrink={0} />
                  <Text fontSize="sm" color="text-secondary">
                    {feature}
                  </Text>
                </HStack>
              ))}
            </SimpleGrid>
            <HStack spacing={8} pt={4} flexWrap="wrap" justify={{ base: 'center', lg: 'flex-start' }}>
              {stats.map((stat, idx) => (
                <VStack key={idx} align={{ base: 'center', lg: 'start' }} spacing={1}>
                  <Text fontSize="xl" fontWeight="700" color="brand.500">
                    {stat.value}
                  </Text>
                  <Text fontSize="sm" color="text-secondary">
                    {stat.label}
                  </Text>
                </VStack>
              ))}
            </HStack>
          </VStack>
          <Box
            borderRadius="2xl"
            overflow="hidden"
            h="500px"
            order={{ base: 1, lg: reverse ? 1 : 2 }}
            boxShadow="lg"
          >
            <Image
              src={imageSrc}
              alt={title}
              w="full"
              h="full"
              objectFit="cover"
            />
          </Box>
        </Grid>
      </Container>
    </Box>
  );
}

// Feature Card Component
interface FeatureCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
}

function FeatureCard({ icon, title, description }: FeatureCardProps) {
  const cardGradient = useColorModeValue(
    'linear(to-br, white, brand.50)',
    'linear(to-br, gray.800, gray.700)'
  );

  return (
    <Card h="full" bgGradient={cardGradient} borderWidth="1px" borderColor="border-default">
      <CardBody>
        <VStack align="start" spacing={4}>
          <Box
            p={3}
            bg="brand.50"
            _dark={{ bg: 'gray.700' }}
            borderRadius="lg"
            display="inline-flex"
          >
            <Icon as={icon} w={6} h={6} color="brand.500" />
          </Box>
          <Heading size="md" color="text-primary">
            {title}
          </Heading>
          <Text color="text-secondary">{description}</Text>
        </VStack>
      </CardBody>
    </Card>
  );
}

// Benefit Item Component
interface BenefitItemProps {
  title: string;
  description: string;
}

function BenefitItem({ title, description }: BenefitItemProps) {
  return (
    <HStack align="start" spacing={4}>
      <Icon as={FiCheckCircle} color="brand.500" w={6} h={6} mt={1} flexShrink={0} />
      <VStack align="start" spacing={1}>
        <Text fontSize="lg" fontWeight="600" color="text-primary">
          {title}
        </Text>
        <Text color="text-secondary">{description}</Text>
      </VStack>
    </HStack>
  );
}

